import { Component, Vue } from "vue-property-decorator";
import {
  cancelHighLight,
  highLight,
  markCorrection,
  removeAllMark,
} from "@/common/CorrectHelper";
import { Delta, QuillEditor } from "@/common/entity/QuillEditor";
import { logToConsole } from "@/common/LogHelper";
import {
  Contents,
  correctionMarkId,
  ICorrectInfo,
} from "@/pages/check/CheckPage";
import { ICorrectResult } from "@/common/entity/CorrectResult";
import {
  htmlStartWithWrap,
  htmlTextSplitLines,
  isEmpty,
} from "@/common/StringHelper";
import { Message } from "element-ui";

@Component
export default class CorrectMarkMixin extends Vue {
  /**
   * 包含错误标记的文章html格式内容
   * 标记完纠错信息后会把有了标记的html格式文本存储到{@link correctHtml}中，但之后如果用户
   * 手动修改编辑框内容是没有立马更新到{@link correctHtml}中的，而是把用户
   * 的修改存储到{@link userDelta}中，可调用{@link updateAndRemarkHtml()}方法把用户修改
   * 更新到{@link correctHtml}中
   */
  correctHtml: string = "";
  /**
   * 用户手动修改但未更新到this.correctHtml中的delta，
   * 最新的delta在数组的最前面，即下标为0的位置
   */
  userDeltas: Delta[] = [];
  /**
   * 编辑器quill实例
   */
  qEditor: QuillEditor | null = null;
  /**
   * 最后一次用于标记的纠错结果
   */
  correctResults: ICorrectResult[][] = [];
  /**
   * 当前被高亮的纠错信息
   */
  currHighLight: ICorrectInfo | null = null;
  /**
   * clipboard matcher 是否执行，（解决由于调用pasteHTML触发clipboard matcher的问题）
   */
  matcherIsRun: Boolean = true;

  /**
   * 设置编辑框内容为{@link html}，并标出{@link correctResults}对应的纠错语句
   * @param correctResults 二维数组，第一层表示第几行，第二层表示在当行中的纠错信息
   */
  markCorrections(correctResults: ICorrectResult[][], html: string) {
    // 初始化纠错信息数组
    this.correctResults = correctResults;

    if (correctResults.length === 0) {
      this.correctHtml = html;
      this.setQeditorHtml(html);
      return;
    }

    /**
     * 分割html文本 (此方法后面的注释中的<分割符>是指：一个或多个连在一起的p标签(<p...></p>)或br标签(<br...>))
     * 这里的htmlLines包括了<分割符>，分割方法看{@link htmlTextSplitLines}
     */
    const htmlIsEmpty = isEmpty(html, true);
    const htmlLines: string[] = htmlIsEmpty
      ? []
      : htmlTextSplitLines(html, true);

    /**
     * 判断html文本是否以<分隔符>开头，当纠错文本以换行开头时，纠错结果{@link correctResults}的第一个元素对应
     * 的是html文本中的第一个段落而不过文章前的换行，而分割以换行开头(html格式是以<分隔符>开头)的文本时，分割
     * 结果{@link htmlLines}的第一个元素(可以说是第一个段落)是一个空字符串，第三个元素才对应纠错结果{@link correctResults}的
     * 第一个元素({@link htmlLines}的第二个元素是<分隔符>)，因此这里记录纠错结果{@link correctResults}和
     * html分割结果{@link htmlLines}段落对应的“偏移量”
     */
    const indexOffset = htmlStartWithWrap(html) ? 1 : 0;

    // 标记对应的纠错语句
    logToConsole("before mark htmlLines=", htmlLines);
    correctResults.forEach((correctioinsInLine, lineIndex) => {
      correctioinsInLine.forEach((correctResult, correctIndex) => {
        if (!correctResult.show) return;
        /**
         * 计算纠错结果对应在分割结果{@link htmlLines}中的位置，加{@link indexOffset}的原因前面说了，
         * 乘以2是因为分割结果{@link htmlLines}包含了<分隔符>，分割方法看{@link htmlTextSplitLines}
         */
        const indexInLines = (lineIndex + indexOffset) * 2;
        const htmlLine = htmlLines[indexInLines];
        if (!htmlLine) return;

        // 标记并替换对应位置的html
        htmlLines[indexInLines] = markCorrection(
          htmlLine,
          correctResult,
          correctionMarkId(lineIndex, correctIndex),
          !!this.currHighLight && //判断是否高亮
            this.currHighLight.firstIndex === lineIndex &&
            this.currHighLight.secondIndex === correctIndex,
        );
      });
    });

    logToConsole("after mark htmlLines=", htmlLines);
    // 把标记后的html数组重新拼装
    this.correctHtml = htmlLines.join("");
    this.setQeditorHtml(this.correctHtml);
  }

  /**
   * 如果有用户手动修改未更新到{@link correctHtml}中，则把内容添加到
   * {@link correctHtml}中，并重新标记内容
   * @see correctHtml
   */
  updateAndRemarkHtml() {
    if (!this.shouldUpdateAndRemark()) return;
    //需要先清除所有红色标记，否则会造成重复标记
    const notMarkHtml = removeAllMark(this.correctHtml);
    this.setQeditorHtml(notMarkHtml);
    //检查用户是否有手动修改
    //重新添加用户改变的内容
    this.updateQEditorContent(() => {
      let userDelta;
      while ((userDelta = this.userDeltas.pop())) {
        logToConsole("update delta=", userDelta);
        this.qEditor?.updateContents(userDelta);
      }
    });
    const currContent = this.parseCurrContent(false);
    this.markCorrections(this.correctResults, currContent.html);
  }

  /**
   * 获取当前编辑器中去掉纠错标记的html格式文本
   */
  getCurrHtmlNoMark(): string {
    if (this.hasCorrections()) {
      this.updateAndRemarkHtml();
      return removeAllMark(this.correctHtml);
    } else return this.getQEditorCurrHtml();
  }

  /**
   * 在编辑器中高亮指定的纠错语句
   * @param correction 要高亮的纠错结果
   * @param firstIndex 要高亮的纠错结果对应在{@link correctResults}数组中的第一层的下标
   * @param secondIndex 要高亮的纠错结果对应在{@link correctResults}数组中的第二层的下标
   */
  highLightCorrection(
    correction: ICorrectResult,
    firstIndex: number,
    secondIndex: number,
  ) {
    if (this.shouldUpdateAndRemark()) {
      this.currHighLight = { ...correction, firstIndex, secondIndex };
      this.updateAndRemarkHtml();
      return;
    }

    //切割html段落
    let htmlLines = htmlTextSplitLines(this.correctHtml, true);
    const indexOffset = htmlStartWithWrap(this.correctHtml) ? 1 : 0;
    const lineIndex = (firstIndex + indexOffset) * 2;

    logToConsole("highLightCorrection before hl htmlLines=", htmlLines);

    //取消高亮
    htmlLines = this.cancelCurrHighLight(htmlLines, indexOffset);

    //高亮显示
    let htmlLine = htmlLines[lineIndex];
    if (!htmlLine) {
      this.showNotFound(firstIndex);
      return;
    }
    htmlLine = highLight(
      htmlLine,
      correction,
      correctionMarkId(firstIndex, secondIndex),
    );
    htmlLines[lineIndex] = htmlLine;

    //设置高亮后的文章内容
    logToConsole("clickCorrectItem after hl htmlLines=", htmlLines);
    this.correctHtml = htmlLines.join("");
    this.setQeditorHtml(this.correctHtml);
    this.currHighLight = { ...correction, firstIndex, secondIndex };
  }

  /**
   * 取消正在显示的高亮错误文本
   * @param htmlLines 分割好段落的html格式文本，为空时调用{@link updateAndRemarkHtml()}分割{@link correctHtml}得到
   * @param indexOffset {@link htmlLines}和{@link correctResults}对应的行数偏移（详情请看分割方法{@link markCorrections}）
   * @returns 取消高亮后的{@link htmlLines}
   */
  cancelCurrHighLight(
    htmlLines: string[] | null = null,
    indexOffset = 0,
  ): string[] {
    markCorrection;
    this.updateAndRemarkHtml();
    if (htmlLines === null) {
      htmlLines = htmlTextSplitLines(this.correctHtml, true);
      indexOffset = htmlStartWithWrap(this.correctHtml) ? 1 : 0;
    }
    if (!this.currHighLight) return htmlLines;
    const firstIndex = this.currHighLight.firstIndex;
    const secondIndex = this.currHighLight.secondIndex;
    const lineIndex = (firstIndex + indexOffset) * 2;
    logToConsole("cancelCurrHighLight firstIndex=", firstIndex);
    logToConsole("cancelCurrHighLight secondIndex=", secondIndex);
    logToConsole("cancelCurrHighLight lineIndex=", lineIndex);
    const lineCorrectioins = this.correctResults[firstIndex];
    if (!lineCorrectioins || !lineCorrectioins[secondIndex]) {
      this.showNotFound(firstIndex);
      return htmlLines;
    }
    const hlCorrection = lineCorrectioins[secondIndex];
    let htmlLine = htmlLines[lineIndex];
    if (!htmlLine) return htmlLines;
    htmlLine = cancelHighLight(
      htmlLine,
      hlCorrection,
      correctionMarkId(firstIndex, secondIndex),
    );
    htmlLines[lineIndex] = htmlLine;
    return htmlLines;
  }

  /**
   * 在{@link correctResults}中找不到对应纠错结果时调用，显示对应的错误提示给用户
   * @param firstIndex 纠错结果对应在{@link correctResults}数组中的第一层的下标
   */
  showNotFound(firstIndex: number) {
    Message.error(
      `未找到对应错误位置，错误位置可能在第${firstIndex + 1}个段落`,
    );
  }

  /**
   * 把{@link html}格式文本的内容设置到编辑器中
   * @param html 要设置的html格式文本
   */
  setQeditorHtml(html: string) {
    const self = this;
    this.updateQEditorContent(() => {
      if (html) {
        this.matcherIsRun = false;
        this.qEditor?.pasteHTML(html);
        this.matcherIsRun = true;
      } else this.qEditor?.setText("");
    });
  }

  /**
   * 更新标记框内容时使用此方法可保证光标位置不变
   * @param {function} 更新操作
   */
  updateQEditorContent(updateFn: () => void) {
    const selection = this.qEditor?.getSelection(true);
    updateFn();
    if (!selection) return;
    this.qEditor?.setSelection(selection);
  }

  /**
   * 把编辑器内容设置为空
   */
  setQeditorToEmpty() {
    this.correctHtml = "";
    this.setQeditorHtml("");
  }

  /**
   * 获取当前编辑器的内容
   * @param isNoMark 是否取出纠错标记
   * @returns 当前编辑器的内容
   */
  parseCurrContent(isNoMark = true): Contents {
    return {
      html: isNoMark ? this.getCurrHtmlNoMark() : this.getQEditorCurrHtml(),
      text: this.getText(),
    };
  }

  /**
   * 获取当前编辑器中的html格式的内容
   */
  getQEditorCurrHtml(): string {
    const html = this.qEditor?.root.innerHTML || "";
    return html === "<p><br></p>" ? "" : html;
  }

  /**
   * 获取当前编辑器中的纯文本内容
   */
  getText(): string {
    const text = this.qEditor?.getText() || "";
    return text === "\n" ? "" : text;
  }

  /**
   * 是否有用户手动修改未更新到this.correctHtml中
   */
  hasUserDeltas(): boolean {
    return this.userDeltas.length > 0;
  }

  /**
   * 当前是否有最后一次用于标记的纠错结果
   */
  hasCorrections(): boolean {
    return this.correctResults?.length > 0;
  }

  /**
   * 判断是否应该把用户手动修改{@link userDeltas}更新到{@link correctHtml}中。
   */
  shouldUpdateAndRemark(): boolean {
    return this.hasCorrections() && this.hasUserDeltas();
  }
}
